import {liteClient as algoliasearch} from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {Popover} from 'bootstrap';
import prestashop from 'prestashop';

import {
  configure,
  refinementList,
  pagination,
  hitsPerPage,
} from 'instantsearch.js/es/widgets';
import {connectStats, connectHits} from 'instantsearch.js/es/connectors';

export default class AlgoliaSubcategoryFacets {
  constructor(options = {}) {
    this.options = {
      appId: 'J5E4XV3104',
      apiKey: 'ba0f26ec5c70c7db53158e7b38cfef70',
      indexName: 'prestashop',
      container: '#algolia-facets-container',
      hitsContainer: '#algolia-hits-container',
      facetsContainer: '#facets-container',
      statsContainer: '.filtered-products span',
      paginationContainer: '#algolia-pagination-container',
      clearRefinementsContainer: '#algolia-clear-refinements-container',
      priceAttribute: 'price',
      hitsPerPage: 25,
      maxFacets: 10,
      priorityFacets: [],
      ...options,
    };

    this.stockData = this.options.stockData || [];

    this.searchClient = algoliasearch(this.options.appId, this.options.apiKey);
    const bodyClasses = prestashop.page.body_classes;
    let categoryId = null;

    Object.keys(bodyClasses).some((className) => {
      const match = className.match(/^category-id-(\d+)$/);

      if (match) {
        categoryId = parseInt(match[1], 10);
        return true; // break out of .some()
      }
      return false;
    });
    this.categoryId = categoryId;

    if (this.categoryId) {
      this.init();
    }
  }

  async init() {
    try {
      const facets = await this.discoverFacets();
      this.initInstantSearch(facets);
    } catch (error) {
      console.warn('Error discovering facets:', error);
      this.initInstantSearch({});
    }
  }

  async discoverFacets() {
    const {results} = await this.searchClient.search([
      {
        indexName: this.options.indexName,
        query: '',
        filters: `categories.id:"${this.categoryId}"`,
        facets: ['*'],
      },
    ]);

    return this.processFacets(results[0]?.facets || {});
  }

  // Get feature order from VP Category Features module
  getFeatureOrder() {
    return window.vpCategoryFeatureOrder || [];
  }

  // Get category ID from VP Category Features module
  getVpCategoryId() {
    return window.vpCategoryId || null;
  }

  normalizeFacetName(name) {
    return name
      .toLowerCase() // Make lowercase
      .replace(/^facet_?/, '') // Remove "facet_" or "facet" prefix
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim(); // Remove leading/trailing whitespace
  }

  processFacets(facets) {
    const featureOrder = this.getFeatureOrder();

    // Create a normalized lookup map for feature order from the array
    const normalizedFeatureOrder = {};
    const activeFeatures = new Set();

    featureOrder.forEach((feature) => {
      const normalized = this.normalizeFacetName(feature.name);
      normalizedFeatureOrder[normalized] = feature.position;

      // Only add to active features if it's explicitly active
      if (feature.active) {
        activeFeatures.add(normalized);
      }
    });

    const facetCounts = Object.entries(facets)
      .filter(([name]) => !name.startsWith('_') && !name.includes('categor') && name !== 'objectID')
      .reduce((acc, [name, values]) => {
        const count = values?.count ?? Object.keys(values).length;

        if (count >= 1) {
          // Normalize the facet name for matching
          const normalizedFacetName = this.normalizeFacetName(name);

          // Check if this facet is in the feature order
          const orderPosition = normalizedFeatureOrder[normalizedFacetName];
          const isInFeatureOrder = orderPosition !== undefined;

          // If it's in the feature order but not active, skip it
          if (isInFeatureOrder && !activeFeatures.has(normalizedFacetName)) {
            return acc;
          }

          // If it's in feature order, use that position, otherwise use 999 (end)
          const customOrder = isInFeatureOrder ? orderPosition : 999;

          acc[name] = {
            count,
            isNumeric: false,
            customOrder,
            isFromFeatureOrder: isInFeatureOrder,
          };
        }
        return acc;
      }, {});

    // Sort by custom order first, then by count
    const sortedFacets = Object.entries(facetCounts)
      .sort(([, dataA], [, dataB]) => {
        // If both have custom order, sort by custom order
        if (dataA.customOrder !== 999 && dataB.customOrder !== 999) {
          return dataA.customOrder - dataB.customOrder;
        }
        // If only one has custom order, prioritize it
        if (dataA.customOrder !== 999) return -1;
        if (dataB.customOrder !== 999) return 1;
        // If neither has custom order, sort by count (descending)
        return dataB.count - dataA.count;
      });

    // Build ordered facets object
    const orderedFacets = {};
    let count = 0;

    sortedFacets.forEach(([name, data]) => {
      if (count < this.options.maxFacets) {
        orderedFacets[name] = data;
        count += 1;
      }
    });

    return orderedFacets;
  }

  // Updated addFacetWidgets method to respect feature ordering
  addFacetWidgets(facets) {
    const container = document.querySelector(this.options.facetsContainer);

    if (!container) return;

    // Clear existing content
    container.innerHTML = '';

    // Create a row for facets
    const facetsRow = document.createElement('div');
    facetsRow.className = 'row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3 mb-4';
    container.appendChild(facetsRow);

    // Add facet widgets in the correct order
    Object.entries(facets).forEach(([name]) => {
      const id = `facet-${name.replace(/\./g, '-')}`;

      // Create a column for each facet
      const col = document.createElement('div');
      col.className = 'col';

      facetsRow.appendChild(col);

      // Create the card container for the facet
      const card = document.createElement('div');
      card.className = 'card h-100';

      col.appendChild(card);

      // Create card header
      const cardHeader = document.createElement('div');
      cardHeader.className = 'card-header';

      const headerContent = `<h5 class="card-title mb-0 fs-6">${this.formatAttributeName(name)}</h5>`;

      cardHeader.innerHTML = headerContent;
      card.appendChild(cardHeader);

      // Create card body for the facet content
      const cardBody = document.createElement('div');
      cardBody.className = 'card-body scrollable-facet-body';
      cardBody.id = id;
      card.appendChild(cardBody);

      // Create the refinement list widget
      const widget = refinementList({
        container: `#${id}`,
        attribute: name,
        searchablePlaceholder: 'Search...',
        limit: 1000,
        showMore: false,
        sortBy: ['name:asc'],
        transformItems: (items) => {
          const parseValue = (val) => {
            let split;
            const lowerVal = val.toLowerCase();

            if (lowerVal.includes(' x ')) {
              split = lowerVal.split(' x ');
            } else if (lowerVal.includes(' - ')) {
              split = lowerVal.split(' - ');
            } else {
              split = [lowerVal];
            }
            return split.map((item) => parseFloat(item.replace(/(?!^-)[^0-9.]/g, '')));
          };

          return items.sort((a, b) => {
            const aParsed = parseValue(a.label);
            const bParsed = parseValue(b.label);

            let i = 0;
            const maxLen = Math.max(aParsed.length, bParsed.length);
            while (i < maxLen) {
              const aVal = aParsed[i] || 0;
              const bVal = bParsed[i] || 0;

              if (aVal !== bVal) {
                return aVal - bVal;
              }
              i += 1;
            }

            return 0;
          });
        },
        cssClasses: {
          searchBox: 'mb-2',
          input: 'form-control form-control-sm',
          checkbox: 'form-check-input me-2',
        },
      });

      this.search.addWidgets([widget]);
    });
  }

  initInstantSearch(facets) {
    this.search = instantsearch({
      indexName: this.options.indexName,
      searchClient: this.searchClient,
      routing: false,
    });

    this.addBasicWidgets();
    this.addFacetWidgets(facets);
    this.search.start();
    this.addEventListeners();
  }

  // Add this method to your AlgoliaSubcategoryFacets class

  // Helper method to detect mobile devices
  isMobile() {
    return window.innerWidth < 980;
  }

  // Replace your existing renderTableHits logic in addBasicWidgets() with this:
  addBasicWidgets() {
    const filters = `categories.id:"${this.categoryId}"`;

    this.search.addWidgets([configure({filters, hitsPerPage: this.options.hitsPerPage})]);

    let totalProducts = 0;
    let updatedHits = [];

    if (document.querySelector(this.options.statsContainer)) {
      const renderStats = connectStats(({nbHits}) => {
        if (totalProducts === 0) {
          totalProducts = nbHits;
        }

        const statsContainer = document.querySelector(this.options.statsContainer);

        if (statsContainer) {
          statsContainer.innerHTML = `
          <span>
           FILTER AVAILABLE PRODUCTS (SHOWING ${nbHits.toLocaleString()} OF ${totalProducts.toLocaleString()})
          </span>`;
        }
      });

      this.search.addWidgets([
        hitsPerPage({
          container: '#hits-per-page',
          items: [
            {label: 'Show 25', value: 25, default: true},
            {label: 'Show 50', value: 50},
            {label: 'Show 100', value: 100},
          ],
        }),
      ]);

      const customStatsWidget = connectStats((renderOptions) => {
        const {
          nbHits, hitsPerPage: hitsPerPageValue, page, widgetParams,
        } = renderOptions;

        const start = nbHits === 0 ? 0 : page * hitsPerPageValue + 1;
        const end = Math.min((page + 1) * hitsPerPageValue, nbHits);

        widgetParams.container.innerHTML = `Showing ${start} to ${end} of ${nbHits} entries`;
      });

      this.search.addWidgets([
        customStatsWidget({
          container: document.querySelector('#pagination-hits'),
        }),
      ]);

      this.search.addWidgets([renderStats()]);
    }

    if (document.querySelector(this.options.paginationContainer)) {
      this.search.addWidgets([
        pagination({
          container: this.options.paginationContainer,
          padding: 2,
          showFirst: false,
          scrollTo: document.querySelector('#facets-container'),
          showLast: false,
          cssClasses: {
            root: 'pagination justify-content-center mb-4',
            item: 'page-item',
            link: 'page-link text-primary',
            selectedItem: 'active',
            disabledItem: 'disabled',
          },

        }),
      ]);
    }

    // Updated hits widget with mobile responsiveness
    if (document.querySelector(this.options.hitsContainer)) {
      const renderResponsiveHits = connectHits((renderOptions, isFirstRender) => {
        const {hits, widgetParams} = renderOptions;
        const container = document.querySelector(widgetParams.container);

        if (isFirstRender) {
          container.classList.add('responsive-hits-container');
        }

        if (hits.length === 0) {
          container.innerHTML = `
          <div class="no-results text-center py-4">
            <img class="img-fluid mb-3" src="${prestashop.shop.logo}">
            <p>No products found matching your filters.</p>
            <div class="mt-3">
              <div class="btn btn-primary btn-livechat">Live Chat <i class="bi bi-person-workspace ms-1"></i></div>
              <a href="tel:01233713581" class="ms-2">
                <div class="btn btn-primary">
                  Call us now
                  <i class="bi bi-telephone-fill ms-1"></i>
                </div>
              </a>
            </div>
          </div>
        `;
          return;
        }

        if (this.stockData.length > 0) {
          const stockMap = new Map(this.stockData.map((item) => [item.code, item]));

          updatedHits = hits.map((hit) => {
            const stockItem = stockMap.get(hit.reference);

            return {
              ...hit,
              free_stock: stockItem?.crm_free_stock ?? 0,
              supplier_free_stock: stockItem?.supplier_free_stock ?? 0,
            };
          });
        }

        // Check if mobile
        if (this.isMobile()) {
          this.renderMobileHits(updatedHits, container);
          $('.button-mob-container #line-drawing').addClass('mb-2 w-100');
        } else {
          this.renderDesktopHits(updatedHits, container);
        }
      });

      this.search.addWidgets([
        renderResponsiveHits({
          container: this.options.hitsContainer,
        }),
      ]);
    }
  }

  // Mobile rendering method
  // Updated renderMobileHits method to order attributes by feature order
  renderMobileHits(updatedHits, container) {
  // Get feature order configuration
    const featureOrder = this.getFeatureOrder();

    // Create normalized lookup maps
    const normalizedFeatureOrder = {};
    const activeFeatures = new Set();

    featureOrder.forEach((feature) => {
      const normalized = this.normalizeFacetName(feature.name);
      normalizedFeatureOrder[normalized] = feature.position;

      if (feature.active) {
        activeFeatures.add(normalized);
      }
    });

    let html = '<div class="mobile-hits-container">';

    updatedHits.sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    updatedHits.forEach((hit) => {
      const productImage = hit.image || hit.small_image_url || '';
      const productCode = hit.reference || hit.code || '';
      const productUrl = hit.url || '#';
      const productId = hit.id || hit.objectID;

      let featuresTable = '';
      const orderedFeatures = (hit.features || [])
        .filter((feature) => {
          const normalized = this.normalizeFacetName(feature.name);

          return !normalizedFeatureOrder[normalized] || activeFeatures.has(normalized);
        })
        .sort((a, b) => {
          const normalizedA = this.normalizeFacetName(a.name);
          const normalizedB = this.normalizeFacetName(b.name);

          const orderA = normalizedFeatureOrder[normalizedA] || 999;
          const orderB = normalizedFeatureOrder[normalizedB] || 999;

          // If both have custom order, sort by position
          if (orderA !== 999 && orderB !== 999) {
            return orderA - orderB;
          }
          // If only one has custom order, prioritize it
          if (orderA !== 999) return -1;
          if (orderB !== 999) return 1;
          // If neither has custom order, sort alphabetically
          return a.name.localeCompare(b.name);
        });

      // Start building the features table
      featuresTable = '<table class="mobile-attributes"><tbody>';

      orderedFeatures.forEach((feature) => {
        featuresTable += `<tr><td>${feature.name}</td><td>${feature.value}</td></tr>`;
      });

      // Add Express Stock row to the table (with the truck popover button)
      const freeStock = parseFloat(hit.free_stock) || 0;
      const supplierStock = parseFloat(hit.supplier_free_stock) || 0;
      const totalStock = freeStock + supplierStock;

      if (totalStock > 0) {
        featuresTable += `
        <tr>
          <td>Express Stock</td>
          <td>
            <div class="express-popover-div">
              <button id="${productCode}" class="btn p-0 m-0 border-0 express-stock-info"
                      data-bs-container="body" data-bs-toggle="popover"
                      style="width: 3.5rem!important; min-width:3.5rem!important;"
                      data-bs-trigger="click" data-bs-placement="auto"
                      aria-label="Express Stock Levels"
                      data-bs-original-title="Express Stock Levels">
                <img class="stock-truck" src="${themeAssetsUrl}img/express-stock.svg"
                     style="width: 45px; height: 45px;">
              </button>
            </div>
          </td>
        </tr>`;
      }

      featuresTable += '</tbody></table>';

      html += `
      <div class="d-flex flex-row mobile-related-list py-3 border-bottom">
        <div class="mobile-rel-img-col flex-column d-flex justify-content-center">
          <div class="mobile-related-title">${productCode}</div>
          <img loading="lazy"
               class="img-fluid border"
               alt="${hit.name || productCode}"
               src="${productImage}"
               height="80"
               width="80">
        </div>
        <div class="d-flex justify-content-center mx-auto">
          ${featuresTable}
        </div>
        <div class="mobile-rel-buttons-col flex-column d-flex justify-content-between">
          <a href="${productUrl}" aria-label="link to product page" class="btn btn-sm btn-info text-white">View</a>
          <button
            aria-label="add to cart"
            class="btn btn-sm btn-danger text-white add-to-cart-btn"
            data-product-id="${productId}"
          >
            Buy Now
          </button>
          <button
            class="btn btn-sm btn-primary text-white request-sample"
            data-sample="${hit.objectID}"
            aria-label="add sample"
          >Sample</button>
        </div>
      </div>
    `;
    });

    html += '</div>';
    container.innerHTML = html;

    this.addStockListeners?.();
  }

  // Desktop rendering method (your existing table logic)
  // Updated renderDesktopHits method to order columns by feature order
  renderDesktopHits(hits, container) {
    container.classList.add('table-responsive');

    // Get feature order configuration
    const featureOrder = this.getFeatureOrder();

    // Create normalized lookup maps
    const normalizedFeatureOrder = {};
    const activeFeatures = new Set();

    featureOrder.forEach((feature) => {
      const normalized = this.normalizeFacetName(feature.name);
      normalizedFeatureOrder[normalized] = feature.position;

      if (feature.active) {
        activeFeatures.add(normalized);
      }
    });

    // Collect all unique attribute names across hits
    const attributeNames = new Set();

    hits.forEach((hit) => {
      if (hit.features && Array.isArray(hit.features)) {
        hit.features.forEach((feature) => {
          const normalized = this.normalizeFacetName(feature.name);

          // Only include if it's active in feature order, or not in feature order at all
          if (!normalizedFeatureOrder[normalized] || activeFeatures.has(normalized)) {
            attributeNames.add(feature.name);
          }
        });
      }
    });

    // Convert to array and sort by feature order
    const allAttributes = Array.from(attributeNames).sort((a, b) => {
      const normalizedA = this.normalizeFacetName(a);
      const normalizedB = this.normalizeFacetName(b);

      const orderA = normalizedFeatureOrder[normalizedA] || 999;
      const orderB = normalizedFeatureOrder[normalizedB] || 999;

      // If both have custom order, sort by position
      if (orderA !== 999 && orderB !== 999) {
        return orderA - orderB;
      }
      // If only one has custom order, prioritize it
      if (orderA !== 999) return -1;
      if (orderB !== 999) return 1;
      // If neither has custom order, sort alphabetically
      return a.localeCompare(b);
    });

    allAttributes.push('Express Stock'); // Ensure stock is always included

    let html = `
    <table class="table table-striped table-hover variants-table align-middle">
      <thead class="table-light">
        <tr>
          <th scope="col" class="text-center">Code</th>`;

    // Add all dynamic attribute headings in the correct order
    allAttributes.forEach((attr) => {
      html += `<th scope="col" class="text-center">${attr}</th>`;
    });

    html += `      
     <th colspan="3"></th>
    </tr>
    </thead>
    <tbody>`;

    hits.sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    hits.forEach((hit) => {
      html += `
      <tr class="text-center">
        <td>${hit.reference || ''}</td>`;

      // Add attribute values in the same order as headers
      allAttributes.forEach((attr) => {
        if (attr === 'Express Stock') {
          let expressStockContent = '';

          if ((parseFloat(hit.free_stock) || 0) > 0 || (parseFloat(hit.supplier_free_stock) || 0) > 0) {
            expressStockContent = `
        <div class="express-popover-div">
          <button id="${hit.reference}" class="btn top-0 end-0 p-0 m-0 border-0 express-stock-info" 
                  data-bs-container="body" data-bs-toggle="popover" 
                  style="width: 3.5rem!important; min-width:3.5rem!important;" 
                  data-bs-trigger="click" data-bs-placement="auto" 
                  aria-label="Express Stock Levels" 
                  data-bs-original-title="Express Stock Levels">
            <img class="stock-truck" src="${themeAssetsUrl}img/express-stock.svg" style="width: 45px; height: 45px;">
          </button>
        </div>`;
          }
          html += `<td>${expressStockContent}</td>`;
          return;
        }
        const match = hit.features?.find((f) => f.name === attr);
        const value = match ? match.value : '';
        html += `<td>${value}</td>`;
      });

      html += `
       <td>
        <button class="btn btn-sm btn-info">
          <a
            class="text-decoration-none text-white"
            href="${hit.url || '#'}"
          >
            View Product
          </a>
        </button>  
        </td>
    <td><button class="btn btn-sm btn-success add-to-cart-btn" 
                data-product-id="${hit.id || hit.objectID}">
          <i class="bi bi-cart-plus"></i> Buy Now
        </button></td>
        <td>
        <button class="btn btn-sm btn-primary request-sample" data-sample="${hit.objectID}">Sample</button>
      </td>
    </tr>
  `;
    });

    html += '</tbody></table>';
    container.innerHTML = html;

    this.addStockListeners();
  }

  // Update your addEventListeners method to handle window resize
  addEventListeners() {
    const toggle = document.querySelector('.facet-toggle-btn');
    const container = document.querySelector(this.options.facetsContainer);
    const clearFilterBtns = document.querySelectorAll('.clear-filters-btn');

    if (toggle && container) {
      toggle.addEventListener('click', () => {
        container.classList.toggle('facets-active');
      });
      document.addEventListener('click', (e) => {
        if (
          window.innerWidth < 992
          && container.classList.contains('facets-active')
          && !container.contains(e.target)
          && !toggle.contains(e.target)
        ) {
          container.classList.remove('facets-active');
        }
      });
    }

    // Handle window resize to re-render hits appropriately
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Trigger re-render of hits when window is resized
        this.search.refresh();
      }, 250);
    });

    this.search.on('render', () => {
      const lists = document.querySelectorAll('.ais-RefinementList');
      let anyChecked = false;

      lists.forEach((list) => {
        const checkboxes = list.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach((checkbox) => {
          if (checkbox.checked) {
            anyChecked = true;
          }
        });
      });

      clearFilterBtns.forEach((btn) => {
        btn.classList.toggle('d-none', !anyChecked);
      });
    });

    clearFilterBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const lists = document.querySelectorAll('.ais-RefinementList');

        lists.forEach((list) => {
          const checkboxes = list.querySelectorAll('input[type="checkbox"]');
          checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
          });
        });

        clearFilterBtns.forEach((b) => b.classList.add('d-none'));

        this.search.helper.clearRefinements().search();
      });
    });

    // Smooth scroll on "view results" click
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('view-results')) {
        e.preventDefault();
        const tableHead = document.querySelector('.product-table-heading');
        tableHead.scrollIntoView({behavior: 'smooth'});
      }
    });
  }

  addStockListeners() {
    const truckButtons = document.querySelectorAll('.express-stock-info');
    const paginationButtons = document.querySelectorAll('.ais-Pagination-item');
    truckButtons.forEach((button) => {
      const productId = button.id;

      // Event listener for each truck button
      button.addEventListener('click', () => {
        this.expressPopover(productId);
      });

      // Remove any existing popover on pagination click
      paginationButtons.forEach((paginationButton) => {
        paginationButton.addEventListener('click', () => {
          document.querySelectorAll('.popover').forEach((popover) => {
            if (popover) {
              popover.remove();
            }
          });
        });
      });
    });
  }

  expressPopover(productId) {
    const button = document.getElementById(productId);

    if (!button) return;

    // Used to ensure correct content is fetched
    const URLParams = new URLSearchParams({
      view: 'pseudo_product_stock',
    });

    // Remove any existing popovers
    document.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => {
      const instance = Popover.getInstance(el);

      if (instance) {
        instance.dispose();
      }
    });

    fetch(`https://shop.vital-parts.co.uk/products/stock-level?code=${productId}&${URLParams.toString()}`)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((data) => {
        const popover = new Popover(button, {
          content: data,
          html: true,
          trigger: 'manual',
          placement: 'auto',
          container: 'body',
        });

        popover.show();
      })
      .catch((err) => {
        const popoverInstance = Popover.getInstance(button);

        if (popoverInstance) {
          popoverInstance.setContent({'.popover-body': 'Error loading stock data'});
        }
      });
  }

  formatAttributeName(name) {
    // Remove any prefix like "facet.", "facet_", or "facet" followed by any non-word char
    const cleaned = name.replace(/^facet[._-]?/i, '');

    // Replace underscores with spaces
    const withSpaces = cleaned.replace(/_/g, ' ');

    // Capitalise each word
    const humanized = withSpaces.replace(/\b\w/g, (char) => char.toUpperCase());

    return humanized;
  }
}
